import React from "react";
import { Link } from "react-router-dom";
import { Users, Globe, ShieldCheck, Heart, Compass, Scale, Eye, Sparkles } from "lucide-react";

const teamImg = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3OTc5ODUzMnww&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: Users, title: "Persönlich", text: "Sie haben einen festen Ansprechpartner – kein Callcenter, keine wechselnden Bearbeiter." },
  { icon: Globe, title: "DACH-fokussiert", text: "Wir kennen die Steuersysteme der Schweiz, Deutschlands, Österreichs und Liechtensteins." },
  { icon: ShieldCheck, title: "Schweizer Diskretion", text: "Verschwiegenheit, Sorgfalt und Präzision sind selbstverständlich." },
  { icon: Heart, title: "Langfristig", text: "Wir denken in Jahrzehnten, nicht in Quartalen. Das spüren Sie." },
];

export default function AboutPage() {
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      <div className="mbw-container">
        <div className="grid lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-7">
            <div className="mbw-overline mb-4">Über uns</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
              Schweizer Treuhand, <br />
              <span className="text-[#991B1B]">deutsch denkend.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-5">
              Die MBW Swiss AG ist Ihr Partner für Treuhand, Steuerberatung und
              Firmengründung in der Schweiz – mit besonderem Fokus auf Mandantinnen und
              Mandanten aus Deutschland, Österreich und Liechtenstein.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Wir verbinden Schweizer Präzision mit dem Verständnis für die Anforderungen
              im deutschsprachigen Raum. Vom Erstgespräch bis zum Verwaltungsmandat
              begleiten wir Sie aus einer Hand.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={teamImg} alt="MBW Team" className="rounded-2xl shadow-xl w-full h-[440px] object-cover" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <div key={v.title} className="mbw-card p-8" data-testid={`value-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-[#1A2433] flex items-center justify-center mb-4">
                <v.icon className="w-6 h-6 text-[#DC2626]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2433] mb-2">{v.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <Link to="/kontakt" className="btn-primary">Lernen Sie uns kennen</Link>
        </div>
      </div>

      {/* Firmenphilosophie - detailed */}
      <section className="mt-24 py-24 md:py-32 bg-[#1A2433] text-white" data-testid="about-philosophy">
        <div className="mbw-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="mbw-overline mb-4 !text-[#DC2626]">Unsere Philosophie</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05] mb-6">
              Treuhand ist ein <span className="text-[#DC2626]">Versprechen.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Das Wort „Treuhand" trägt einen Anspruch in sich: jemand legt etwas in unsere
              Hand und vertraut darauf, dass wir es sorgfältig verwalten. Genau so verstehen
              wir unsere Arbeit – als persönliches Versprechen an jeden Mandanten.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Diese Philosophie prägt jeden Schritt: Wie wir Erstgespräche führen, wie wir
              Mandate annehmen und wie wir mit Ihren Daten umgehen.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Heart, title: "Mensch vor Mandat", text: "Wir interessieren uns für Ihre Geschichte, nicht nur Ihre Zahlen. Erstgespräche sind kostenlos und unverbindlich – immer." },
              { icon: ShieldCheck, title: "Diskretion", text: "Schweizer Berufsgeheimnis ist mehr als ein Gesetz – es ist Grundhaltung. Was bei uns landet, bleibt bei uns." },
              { icon: Compass, title: "Klarheit", text: "Wir verzichten auf Fachjargon. Sie sollen verstehen, was wir tun, warum wir es so tun und welche Alternativen es gibt." },
              { icon: Scale, title: "Langfristigkeit", text: "Wir denken in Jahrzehnten, nicht in Quartalen. Eine gute Struktur trägt – auch wenn sich das Geschäft verändert." },
              { icon: Eye, title: "Vorausschau", text: "Steuern und Strukturen sind keine Reaktion auf Ergebnisse, sondern Planung im Voraus. Wir schauen mit Ihnen nach vorn." },
              { icon: Sparkles, title: "Modern & menschlich", text: "Digitale Werkzeuge nehmen Routine ab. Die Beratung bleibt persönlich – immer mit einem Gegenüber, nicht mit einem Ticketsystem." },
            ].map((p, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors" data-testid={`about-philosophy-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#DC2626] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{p.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
